import axios from 'axios';
import type { AssemblyView } from './assemblyScene';
import { drawClipFrame } from './ClipPlayer';
import { CLIP_SPEED, clipAssembly, clipMs, clipScene, type Clip } from './assemblyClip';
import { saveBlob } from './videoRecorder';

// THE WHOLE ASSEMBLY AS AN ANIMATED GLB, from the All view (Louis 2026-10-29): every sub-step played
// frame by frame as the video export plays them, and where every mesh stands noted at each frame,
// rather than a picture of it. The server writes each mesh file once and these frames as the file's
// one animation (see glb.animated_assembly).
//
// NO CAMERA, AND THE ASSEMBLY STANDS STILL: each sub-step's scene stands the chair its own way, and
// every one of them is put in the FIRST one's frame, through the mesh two neighbours both hold where
// it stays (see sharedMeshes). So the parts come to the assembly; nothing turns it round.
const FPS = 30;

// THE MESHES `clip` STARTS WITH THAT THE ONE BEFORE IT ENDED WITH: its fixed piece, which the one
// before had standing as its fixed part or its moving one set home. Where one stands in each scene is
// the change of frame between them.
export const sharedMeshes = (clip: Clip) => clipScene(clip).filter((p) => p.instanceId === 'target')
  .flatMap((p) => (p.pieces?.length ? p.pieces.map((q) => q.url) : [p.url]));

export async function downloadAssemblyGlb(o: {
  clips: Clip[];
  name: string;
  // Puts sub-step k on screen (and builds the next one behind it).
  open: (k: number) => void;
  // Sub-step k's scene, once built.
  viewer: (k: number) => Promise<AssemblyView>;
  stopped: () => boolean;
  onProgress: (done: number) => void;
}) {
  const THREE = await import('three');
  // FROM THE SUB-STEP ON SCREEN'S FRAME INTO THE FIRST ONE'S.
  const into = new THREE.Matrix4();
  const m = new THREE.Matrix4();
  const at = new THREE.Vector3();
  const turn = new THREE.Quaternion();
  const size = new THREE.Vector3();
  const r = (x: number) => Math.round(x * 1e5) / 1e5;
  const clips: { start: number; length: number }[] = [];
  const instances: { url: string; clip: number; name: string; trs: number[] }[] = [];
  let start = 0;
  for (const [k, clip] of o.clips.entries()) {
    o.open(k);
    const v = await o.viewer(k);
    const asm = clipAssembly(clip, true);
    const length = clipMs(clip.movement) / CLIP_SPEED / 1000;
    const count = Math.max(2, Math.round(length * FPS) + 1);
    const rows: { url: string; inst: string; trs: number[] }[] = [];
    for (let f = 0; f < count; f += 1) {
      if (o.stopped()) throw new Error('cancelled');
      drawClipFrame(v, asm, clip, f / (count - 1), false);
      v.meshFrames().forEach((mesh, j) => {
        rows[j] ??= { url: mesh.url, inst: mesh.inst, trs: [] };
        m.fromArray(mesh.frame).premultiply(into).decompose(at, turn, size);
        if (!mesh.shown) size.set(0, 0, 0);
        rows[j].trs.push(...[at.x, at.y, at.z, turn.x, turn.y, turn.z, turn.w, size.x, size.y, size.z].map(r));
      });
      // The page breathes: a long sub-step is thousands of frames.
      if (f % 15 === 0) await new Promise((done) => setTimeout(done, 0));
    }
    rows.forEach((row) => instances.push({ url: row.url, clip: k, name: `${k + 1} ${row.inst}`, trs: row.trs }));
    clips.push({ start, length });
    start += length;
    o.onProgress((k + 1) / o.clips.length);
    // THE NEXT ONE'S FRAME, IN THIS ONE'S: where a shared mesh stands at this one's end and at that
    // one's start.
    const next = o.clips[k + 1];
    if (!next) continue;
    const nv = await o.viewer(k + 1);
    drawClipFrame(nv, clipAssembly(next, true), next, 0, false);
    const linked = sharedMeshes(next).some((url) => {
      const here = v.meshFrame(url, ['target', 'moving']);
      const there = nv.meshFrame(url, ['target']);
      if (!here || !there) return false;
      into.multiply(new THREE.Matrix4().fromArray(here)).multiply(new THREE.Matrix4().fromArray(there).invert());
      return true;
    });
    // SAID, NOT HIDDEN: without a shared mesh the next one stands in its own frame, and the file jumps there.
    if (!linked) console.warn(`[assembly glb] sub-step ${k + 2} shares no mesh with ${k + 1}: it is not carried into its frame`);
  }
  const { data } = await axios.post<Blob>('/api/notice/3d/assembly-glb', { name: o.name, clips, instances }, { responseType: 'blob' });
  saveBlob(data, `${o.name}.glb`);
}
